import { PPPaymentInfoService } from '@openeducation/pp-rn-services';
import type {
  Installment,
  CreditCard,
  Currency,
  Organization,
} from '@openeducation/pp-rn-shared-types';
import { useQuery } from 'react-query';

import { useEnvironmentContext } from '../../environment/environment.provider';
import { QueryKey } from '../../types';

interface UseGetInstallmentsInput {
  cardType?: CreditCard;
  amount?: number;
  billingFrequency?: string;
  currency?: Currency;
  initialTerm?: number;
  organization?: Organization;
  cardBinNumber?: string;
  splitInvoice?: boolean;
  isB2B?: boolean;
}

export const useGetInstallments = ({
  cardType = '' as CreditCard,
  amount = 0,
  currency = '' as Currency,
  organization = '' as Organization,
  billingFrequency,
  initialTerm,
  cardBinNumber = '',
  splitInvoice,
  isB2B = false,
}: UseGetInstallmentsInput) => {
  const { environment } = useEnvironmentContext();

  const requiredInputParams = [
    cardType,
    amount,
    currency,
    organization,
    cardBinNumber,
    ...(isB2B ? [] : [billingFrequency, initialTerm]),
  ];

  const someParamsAreEmpty = requiredInputParams.some((item) => !item);
  const isCardBinOk = cardBinNumber ? cardBinNumber.length === 6 : true;

  const additionalQueryKeyParams = isB2B ? [isB2B] : [splitInvoice, isB2B];

  const queryKeyParams = [...requiredInputParams, ...additionalQueryKeyParams];

  const { data, isError, isLoading } = useQuery<Installment[]>(
    [QueryKey.GetInstallments, ...queryKeyParams],
    () =>
      PPPaymentInfoService.getInstallments({
        env: environment,
        cardType,
        amount,
        billingFrequency,
        currency,
        initialTerm,
        organization,
        cardBinNumber,
        splitInvoice,
        isB2B,
      }),
    { enabled: isCardBinOk && !someParamsAreEmpty }
  );

  return {
    data,
    isError,
    isLoading,
  };
};
